// Window manager: otevírání oken podle popisu ze serveru, pořadí, fokus, režimy (PC / tablet / mobil),
// uložení a obnova rozložení per uživatel a režim. Okna nikdy nevědí nic o jiných oknech.
import { action, core, ApiError } from '../api';
import type { WindowDescriptor, Mode } from '../contract';
import { h, debounce, uid, isTyping, shortcutOf, asObject } from '../dom';
import { t, tk } from '../i18n';
import { toast, toastError } from '../components/toast';
import { errorState, spinner } from '../components/misc';
import { IwMenu } from '../components/menu';
import { IwWindow, type WindowView } from './window';
import { createView } from '../views/factory';

export interface OpenOptions {
  app: string;
  window: string;
  params?: Record<string, unknown>;
  modal?: boolean;
  parent?: IwWindow | null;
  onResult?: (result: unknown) => void;
  geometry?: Partial<IwWindow['geometry']>;
  state?: 'normal' | 'maximized' | 'minimized';
  restore?: unknown;
  focus?: boolean;
  /** Otevřít další okno, i když stejné už existuje (Shift/Ctrl/prostřední tlačítko). */
  newInstance?: boolean;
}

export interface LayoutEntry {
  app: string;
  window: string;
  params: Record<string, unknown>;
  geometry: IwWindow['geometry'];
  state: string;
  view?: unknown;
}

export class WindowManager {
  readonly layer: HTMLElement;
  readonly windows: IwWindow[] = [];
  private focused: IwWindow | null = null;
  private mode: Mode = 'pc';
  private restoring = false;
  onChange: (() => void) | null = null;
  onFocusChange: ((w: IwWindow | null) => void) | null = null;
  private save = debounce(() => void this.persist(), 1200);

  constructor(container: HTMLElement) {
    this.layer = h('div', { class: 'windows-layer' });
    container.appendChild(this.layer);
    window.addEventListener('resize', () => this.onViewport());
    document.addEventListener('keydown', (e) => this.onKey(e));
    this.onViewport();
  }

  currentMode(): Mode {
    return this.mode;
  }

  // ---- zpět na telefonu (gesto od okraje / tlačítko zpět, postup 62): systém je posílá prohlížeči jako „zpět v historii“,
  // proto iWan zapisuje své kroky (otevřené okno, režim výběru, hledání, vysunutý panel) do historie a zpět zavře poslední.
  // Na začátku (nic otevřeného) zpět iWan neopustí – jen upozorní. Krok patří oknu (owner): zpět vrací kroky okna, které je
  // vidět, a zavřené / minimalizované okno si své kroky odnese.
  private back: { id: number; run: () => Promise<boolean> | boolean; owner: IwWindow | null }[] = [];
  private backSeq = 0;
  private skipPop = 0;
  private backInit = false;
  private backBusy = false;
  /** krok „zavřít okno“ každého otevřeného okna (telefon) */
  private winBack = new Map<IwWindow, number>();

  /** Zapíše krok (jen telefon); zpět ho vrátí voláním run (false = nešlo, např. neuložené změny). Vrací id pro dropBack. */
  pushBack(run: () => Promise<boolean> | boolean, owner: IwWindow | null = null): number {
    if (this.mode !== 'mobile') return 0;
    this.initBack();
    const id = ++this.backSeq;
    this.back.push({ id, run, owner });
    history.pushState({ iw: id }, '');
    return id;
  }

  /** Je krok ještě v zásobníku? (okno mohlo být mezitím minimalizované – kroky odnese) */
  hasBack(id: number): boolean {
    return id > 0 && this.back.some((b) => b.id === id);
  }

  /** Krok skončil jinak (✕, ←, dokončený výběr): odebrat ze zásobníku i z historie. */
  dropBack(id: number): void {
    this.dropSteps((b) => b.id === id);
  }

  /** Odebere kroky podle podmínky; z historie stejný počet záznamů najednou (jedno „zpět“ prohlížeče). */
  private dropSteps(match: (b: { id: number; owner: IwWindow | null }) => boolean): void {
    const n = this.back.filter(match).length;
    if (!n) return;
    this.back = this.back.filter((b) => !match(b));
    this.skipPop++;
    history.go(-n);
  }

  /** Telefon: okno dostane krok „zavřít“ (otevření, obnovení z minimalizace, přechod z PC na telefon). */
  private addWinBack(win: IwWindow): void {
    if (this.winBack.has(win) || win.state === 'minimized') return;
    const id = this.pushBack(() => win.close(), win);
    if (id) this.winBack.set(win, id);
  }

  /** Okno zmizelo z obrazovky (zavřené, minimalizované): pryč všechny jeho kroky. */
  private dropWinBack(win: IwWindow): void {
    this.winBack.delete(win);
    this.dropSteps((b) => b.owner === win);
  }

  private initBack(): void {
    if (this.backInit) return;
    this.backInit = true;
    history.pushState({ iwRoot: 1 }, '');
    window.addEventListener('popstate', () => void this.onPop());
  }

  private async onPop(): Promise<void> {
    if (this.skipPop > 0) {
      this.skipPop--;
      return;
    }
    // otevřený dialog, nabídka nebo panel: zpět zavře jen je (jako Android), krok okna zůstane
    const dlg = document.querySelector<HTMLDialogElement>('dialog.iw-dialog[open]');
    const overlay = document.querySelector('.ribbon-popover, .bottom-sheet');
    if (dlg || IwMenu.isOpen() || overlay || this.backBusy) {
      if (dlg) dlg.dispatchEvent(new Event('cancel', { cancelable: true })); // jako Zrušit
      else if (!this.backBusy) {
        IwMenu.closeAll();
        overlay?.remove();
      }
      history.pushState({ iw: this.back[this.back.length - 1]?.id ?? 0 }, '');
      return;
    }
    // krok okna, které je vidět (navrchu); jinak poslední zapsaný
    let i = -1;
    for (let k = this.back.length - 1; k >= 0 && i < 0; k--) if (this.back[k]?.owner === this.focused && this.focused) i = k;
    if (i < 0) i = this.back.length - 1;
    const top = this.back[i];
    if (!top) {
      history.pushState({ iwRoot: 1 }, '');
      toast(t('core.ui.back_exit'), 'info');
      return;
    }
    this.back.splice(i, 1);
    const winStep = top.owner !== null && this.winBack.get(top.owner) === top.id;
    if (winStep && top.owner) this.winBack.delete(top.owner);
    this.backBusy = true;
    try {
      if ((await top.run()) === false) { // neuložené změny – okno zůstává, krok taky
        this.back.push(top);
        if (winStep && top.owner) this.winBack.set(top.owner, top.id);
        history.pushState({ iw: top.id }, '');
      }
    } finally {
      this.backBusy = false;
    }
  }

  private onViewport(): void {
    const w = window.innerWidth;
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const mode: Mode = w < 600 ? 'mobile' : w < 1024 || coarse ? 'tablet' : 'pc';
    const changed = mode !== this.mode;
    this.mode = mode;
    document.documentElement.dataset['mode'] = mode;
    if (mode === 'mobile') this.initBack();
    if (changed && mode === 'mobile') for (const win of this.windows) if (win.app !== '__local') this.addWinBack(win);
    if (changed && mode !== 'mobile') {
      this.winBack.clear();
      this.dropSteps(() => true); // na PC zpět prohlížeče okna nezavírá
    }
    for (const win of this.windows) {
      win.forceMaximized(mode === 'mobile');
      win.apply();
      if (changed) win.view?.setMode?.(mode);
    }
    if (changed) this.onChange?.();
  }

  async open(opts: OpenOptions): Promise<IwWindow | null> {
    const params = asObject(opts.params);
    // stejné okno se stejnými parametry → jen do popředí (okna Záznam nového záznamu se otevírají vždy)
    const existing = this.windows.find((w) => w.app === opts.app && w.windowId === opts.window && JSON.stringify(w.params) === JSON.stringify(params) && !w.modal);
    if (existing && (!opts.modal || existing.closeOnSave) && !opts.newInstance) {
      if (existing.state === 'minimized') existing.unminimize();
      else existing.focusWindow();
      return existing;
    }
    const win = new IwWindow(uid());
    win.app = opts.app;
    win.windowId = opts.window;
    win.params = params;
    win.setTitle(t('core.ui.loading'), 'window');
    // záznam otevřený „modálně“ (detail z lišty) je běžné okno, mezi kterým jde přepínat (postup 63);
    // blokující dialog je jen u vlastních oken (nastavení, výběr…) – rozhodne se podle popisu okna níže
    win.closeOnSave = opts.modal === true;
    const area = this.layer.getBoundingClientRect();
    const n = this.windows.length;
    win.geometry = { x: 60 + (n % 8) * 28, y: 40 + (n % 8) * 24, w: Math.min(900, area.width - 40), h: Math.min(600, area.height - 40), ...opts.geometry };
    this.windows.push(win);
    // telefon: zpět zavře okno; minimalizované okno („domů“) krok pro zpět nemá, po obnovení ho dostane znovu
    this.addWinBack(win);
    win.onClose = () => {
      this.dropWinBack(win);
      this.onClosed(win);
    };
    win.onRestore = () => this.addWinBack(win);
    win.onFocus = () => this.setFocus(win);
    win.onChange = () => this.changed();
    win.onMinimize = () => {
      this.dropWinBack(win);
      this.changed();
    };
    this.layer.appendChild(win);
    win.forceMaximized(this.mode === 'mobile');
    if (opts.state === 'maximized') win.maximize();
    win.body().replaceChildren(spinner());
    if (opts.focus !== false) win.focusWindow();
    this.changed();

    try {
      const desc = await action<WindowDescriptor>(opts.app, opts.window, 'describe', params);
      if (!desc.modes.includes(this.mode)) {
        throw new ApiError('mode', 'desktop.shell.mobile_hint', 400);
      }
      win.setTitle(tk(desc.title), desc.icon);
      if (desc.minSize) {
        win.minW = desc.minSize.w;
        win.minH = desc.minSize.h;
        win.apply();
      }
      if (opts.modal && desc.kind !== 'record') {
        win.setModal(opts.parent ?? this.focused);
        win.parent?.classList.add('has-modal');
      }
      if (!opts.geometry && !opts.modal) {
        win.geometry.w = Math.min(desc.size.w, area.width - 20);
        win.geometry.h = Math.min(desc.size.h, area.height - 20);
        win.geometry.x = Math.max(0, Math.round((area.width - win.geometry.w) / 2) + ((n % 5) - 2) * 24);
        win.geometry.y = Math.max(0, Math.round((area.height - win.geometry.h) / 2) + ((n % 5) - 2) * 18);
        win.apply();
      } else if (opts.modal) {
        win.geometry.w = Math.min(desc.size.w, area.width - 20);
        win.geometry.h = Math.min(desc.size.h, area.height - 20);
        const p = win.parent?.geometry;
        win.geometry.x = p ? Math.max(0, p.x + (p.w - win.geometry.w) / 2) : (area.width - win.geometry.w) / 2;
        win.geometry.y = p ? Math.max(0, p.y + (p.h - win.geometry.h) / 2) : (area.height - win.geometry.h) / 2;
        win.apply();
      }
      const view = createView(desc, win, this, {
        close: (result?: unknown) => {
          if (result !== undefined) opts.onResult?.(result);
          void win.close(true);
        },
        result: (r: unknown) => opts.onResult?.(r),
      });
      win.setView(view);
      if (opts.restore !== undefined) view.setState?.(opts.restore);
      if (opts.focus !== false) view.focus?.();
    } catch (e) {
      const err = e as ApiError;
      win.setTitle(t('core.ui.error'), 'warning');
      win.body().replaceChildren(errorState(tk(err.message ?? 'core.err.internal'), undefined, err.id));
      if (err.status === 403 || err.status === 404) toastError(err);
    }
    return win;
  }

  private setFocus(win: IwWindow): void {
    if (this.focused === win) return;
    this.focused?.blurWindow();
    this.focused = win;
    this.onFocusChange?.(win);
  }

  focusedWindow(): IwWindow | null {
    return this.focused;
  }

  private onClosed(win: IwWindow): void {
    IwMenu.closeIn(win); // nabídka zavřeného okna nesmí zůstat viset (nabídky jiných oken ano)
    const i = this.windows.indexOf(win);
    if (i >= 0) this.windows.splice(i, 1);
    if (win.parent) win.parent.classList.remove('has-modal');
    if (this.focused === win) {
      this.focused = null;
      const next = [...this.windows].filter((w) => w.state !== 'minimized').sort((a, b) => Number(b.style.zIndex) - Number(a.style.zIndex))[0];
      if (next) next.focusWindow();
      else this.onFocusChange?.(null);
    }
    this.changed();
  }

  private changed(): void {
    this.onChange?.();
    if (!this.restoring) this.save();
  }

  minimizeAll(): void {
    for (const w of this.windows) if (!w.modal) w.minimize();
  }

  async closeAll(): Promise<void> {
    for (const w of [...this.windows]) await w.close();
  }

  /** Přepínání oken (vlastní zkratka Ctrl+`), cyklicky podle pořadí. */
  cycle(): void {
    const list = this.windows.filter((w) => !w.modal);
    if (!list.length) return;
    const i = this.focused ? list.indexOf(this.focused) : -1;
    const next = list[(i + 1) % list.length];
    if (next) {
      if (next.state === 'minimized') next.unminimize();
      else next.focusWindow();
    }
  }

  private onKey(e: KeyboardEvent): void {
    const sc = shortcutOf(e);
    if (sc === 'Ctrl+`') {
      e.preventDefault();
      this.cycle();
      return;
    }
    // otevřený dialog (potvrzení, dotaz) má klávesy pro sebe – Esc ho zruší, okno pod ním nic nedostane
    if (e.target instanceof Element && e.target.closest('dialog[open]')) return;
    // Esc už zpracovala nabídka (zavřela se) nebo seznam (zrušil výběr) – okno se kvůli tomu nezavírá
    if (sc === 'Esc' && e.defaultPrevented) return;
    // v textovém poli nechat psaní na pokoji – kromě Esc a F-kláves (Ctrl+S apod. projde díky Ctrl)
    if (isTyping(e.target) && !e.ctrlKey && !e.altKey && sc !== 'Esc' && !/^F\d+$/.test(sc)) return;
    if (!this.focused) return;
    // zkratky příkazů aktivního okna
    const view = this.focused.view as (WindowView & { shortcut?: (sc: string) => boolean }) | null;
    if (view?.shortcut?.(sc)) {
      e.preventDefault();
      return;
    }
    if (sc === 'Esc' && (this.focused.modal || this.focused.closeOnSave)) {
      void this.focused.close();
    }
  }

  /** Lokální (systémové) okno bez popisu ze serveru – dialogy jádra jako „Vybrat soubor“. Neukládá se do rozložení. */
  /**
   * Totéž okno s jinými parametry (listování v Prohlížeči, další soubor): znovu popsat a postavit pohled; okno, jeho poloha
   * a pořadí zůstávají. Starý pohled se uklidí (`destroy`). `restore` se předá novému pohledu jako při otevření.
   */
  async reopen(win: IwWindow, params: Record<string, unknown>, restore?: unknown): Promise<void> {
    win.params = asObject(params);
    try {
      const desc = await action<WindowDescriptor>(win.app, win.windowId, 'describe', win.params);
      win.setTitle(tk(desc.title), desc.icon);
      win.resetChrome();
      const view = createView(desc, win, this, { close: () => void win.close(true), result: () => undefined });
      win.setView(view);
      if (restore !== undefined) view.setState?.(restore);
      if (this.focused === win) view.focus?.();
      this.changed();
    } catch (e) {
      const err = e as ApiError;
      toast(tk(err.message ?? 'core.err.internal', err.params ?? undefined), 'error');
    }
  }

  async openLocal(opts: { title: string; icon?: string; size?: { w: number; h: number }; modal?: boolean; parent?: IwWindow | null; build: (win: IwWindow, close: () => void) => WindowView; onClosed?: () => void }): Promise<IwWindow> {
    const win = new IwWindow(uid());
    win.app = '__local';
    win.windowId = 'local';
    win.setTitle(opts.title, opts.icon ?? 'window');
    if (opts.modal) win.setModal(opts.parent ?? this.focused);
    const area = this.layer.getBoundingClientRect();
    const w = Math.min(opts.size?.w ?? 800, area.width - 20);
    const hh = Math.min(opts.size?.h ?? 560, area.height - 20);
    win.geometry = { x: Math.max(0, (area.width - w) / 2), y: Math.max(0, (area.height - hh) / 2), w, h: hh };
    win.minW = Math.min(win.minW, w); // malé lokální okno (Přizpůsobit…) se výchozím minimem nezvětší
    win.minH = Math.min(win.minH, hh);
    this.windows.push(win);
    win.onClose = () => {
      this.onClosed(win);
      opts.onClosed?.();
    };
    win.onFocus = () => this.setFocus(win);
    win.onChange = () => this.onChange?.();
    this.layer.appendChild(win);
    win.forceMaximized(this.mode === 'mobile');
    if (opts.modal && win.parent) win.parent.classList.add('has-modal');
    const view = opts.build(win, () => void win.close(true));
    win.setView(view);
    win.focusWindow();
    view.focus?.();
    this.onChange?.();
    return win;
  }

  // ---- rozložení

  layout(): LayoutEntry[] {
    return this.windows
      .filter((w) => !w.modal && !w.closeOnSave && w.view && w.app !== '__local')
      .map((w) => ({ app: w.app, window: w.windowId, params: w.params, geometry: { ...w.geometry }, state: w.state, view: w.view?.getState?.() }));
  }

  private async persist(): Promise<void> {
    try {
      await core('prefs', 'set', { key: 'layout.' + (this.mode === 'mobile' ? 'mobile' : 'pc'), value: this.layout() });
    } catch {
      /* rozložení není kritické */
    }
  }

  async restoreLayout(entries: LayoutEntry[]): Promise<void> {
    this.restoring = true;
    try {
      for (const e of entries.slice(0, 20)) {
        if (!e.app || !e.window) continue;
        await this.open({ app: e.app, window: e.window, params: asObject(e.params), geometry: e.geometry, state: e.state === 'maximized' ? 'maximized' : 'normal', restore: e.view, focus: false });
        if (e.state === 'minimized') this.windows[this.windows.length - 1]?.minimize();
      }
    } finally {
      this.restoring = false;
    }
    const top = this.windows.filter((w) => w.state !== 'minimized').pop();
    top?.focusWindow();
  }
}
